<template>
  <!--
    The one moment the claim actually completes. The api's confirm endpoint
    redirects here with ?confirmed=1|already (or ?confirm_error=... to the
    landing page), and until now nothing read those — the user followed the
    link from their inbox, landed on a dashboard, and was told nothing at all.
  -->
  <teleport to="body">
    <div
      v-if="open"
      class="confirm-backdrop"
      role="presentation"
      @click.self="close"
    >
      <div
        class="confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="claim-confirmed-title"
      >
        <button
          type="button"
          class="confirm-close"
          :aria-label="$t('claim.close')"
          @click="close"
        >✕</button>

        <div class="confirm-icon" aria-hidden="true">{{ isError ? '⚠️' : '✅' }}</div>
        <h2 id="claim-confirmed-title" class="confirm-heading">{{ $t(`claimConfirmed.${state}Heading`) }}</h2>
        <p class="confirm-body">{{ $t(`claimConfirmed.${state}Body`) }}</p>

        <button type="button" class="confirm-btn" @click="close">
          {{ $t('claimConfirmed.dismiss') }}
        </button>
      </div>
    </div>
  </teleport>
</template>

<script>
export default {
    name: 'ClaimConfirmedNotice',
    data () {
        return {
            // Captured once on mount, before the query is stripped, so the
            // notice survives the router tidying the URL underneath it.
            state: '',
            open: false,
        };
    },
    computed: {
        isError () {
            return this.state === 'expired' || this.state === 'server';
        },
    },
    mounted () {
        const query = this.$route?.query || {};
        const confirmed = typeof query.confirmed === 'string' ? query.confirmed : '';
        const error = typeof query.confirm_error === 'string' ? query.confirm_error : '';

        if (confirmed === '1') {
            this.state = 'success';
        } else if (confirmed === 'already') {
            this.state = 'already';
        } else if (error === 'expired') {
            this.state = 'expired';
        } else if (error === 'server') {
            this.state = 'server';
        }

        if (!this.state) {
            return;
        }

        this.open = true;
        window.addEventListener('keydown', this.onKeydown);

        // Drop the flag from the URL so a refresh (or a shared link) doesn't
        // re-announce a claim that already happened.
        const rest = { ...query };

        delete rest.confirmed;
        delete rest.confirm_error;
        this.$router.replace({ path: this.$route.path, query: rest }).catch(() => { /* redundant nav */ });
    },
    beforeUnmount () {
        window.removeEventListener('keydown', this.onKeydown);
    },
    methods: {
        onKeydown (event) {
            if (event.key === 'Escape') {
                this.close();
            }
        },
        close () {
            this.open = false;
        },
    },
};
</script>

<style scoped>
/* Deliberately mirrors ClaimCard's modal chrome — same surface, radius, and
   backdrop — so the end of the claim flow looks like the rest of it. */
.confirm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.66);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  /* Above LoadingOverlay (9999), same as ClaimCard's backdrop: the confirm
     link can land while the profile is still syncing. */
  z-index: 10000;
}

.confirm-modal {
  position: relative;
  width: 100%;
  max-width: 420px;
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 12px;
  padding: 28px;
  text-align: center;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.55);
}

.confirm-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  color: var(--muted);
  font-size: 1rem;
  cursor: pointer;
  line-height: 1;
  padding: 4px;
}

.confirm-close:hover {
  color: var(--text);
}

.confirm-icon {
  font-size: 2.5rem;
  line-height: 1;
  margin-bottom: 12px;
}

.confirm-heading {
  margin: 0 0 10px;
  font-size: 1.25rem;
  color: var(--text);
}

.confirm-body {
  margin: 0 0 20px;
  color: var(--muted);
  line-height: 1.55;
}

.confirm-btn {
  background: var(--primary);
  color: #0b0e12;
  border: none;
  padding: 11px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s ease;
  width: 100%;
}

.confirm-btn:hover {
  background: var(--primary-hover);
}
</style>
